import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from '../utils/toast';
import TopBar from '../components/TopBar';
import Loading from '../components/Loading';
import { virtualQrPaymentAction } from '../redux/actions/phoneLookupActions';
import { VIRTUAL_QR_PAYMENT_RESET } from '../redux/constants/phoneLookupConstants';

export default function VirtualQrPaymentScreen() {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    
    const { patient, hospital, virtualQrPrice } = location.state || {};
    console.log('Location data', location.state);
    const { loading, paymentData, success } = useSelector(state => state.virtualQrPayment);
    
    useEffect(() => {
        return () => {
            dispatch({ type: VIRTUAL_QR_PAYMENT_RESET });
        };
    }, [dispatch]);

    // Redirect if no patient data
    useEffect(() => {
        if (!patient || !hospital) {
            toast.error('Missing patient or hospital information');
            navigate('/register');
        }
        // set patient data to locastorage
        sessionStorage.setItem('patient', JSON.stringify(patient));
    }, [patient, hospital, navigate]);

    // Handle payment success - redirect to Paystack
    useEffect(() => {
        if (success && paymentData?.data?.paystack_data?.authorization_url) {
            const authUrl = paymentData.data.paystack_data.authorization_url;
            const reference = paymentData.data.reference;

            sessionStorage.setItem('paymentData', JSON.stringify({
                reference: reference,
                patient: localStorage.getItem('patient'),
                hospital,
                virtualQrPrice
            }));
            
            toast.success('Redirecting to payment...');
            window.location.href = authUrl;
        }
    }, [success, paymentData, patient, hospital, virtualQrPrice]);



    const handleInitiatePayment = async () => {
        if (!patient || !hospital) {
            toast.error('Patient information not found');
            return;
        }

        const callbackUrl = `${window.location.origin}/payment-return`;
        const patientPhone = patient.mobile || '0000000000';
        const patientEmail = (patient.email && patient.email.includes('@'))
            ? patient.email
            : `patient_${hospital.id}_${patient.id}@misas.ng`;

        dispatch(virtualQrPaymentAction(
            hospital.id,
            patient.id,
            patient.system_id,
            patientPhone,
            patientEmail,
            callbackUrl
        ));
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-NG', {
            style: 'currency',
            currency: 'NGN'
        }).format(amount);
    };

    if (!hospital || !patient) {
        return (
            <div className='min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center'>
                <Loading />
            </div>
        );
    }

    if (loading) {
        return (
            <div className="fixed inset-0 bg-white z-50 flex flex-col items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary100 mx-auto mb-4"></div>
                    <h2 className="text-xl font-semibold text-gray-800 mb-2">Initiating payment...</h2>
                </div>
            </div>
        );
    }

    return (
        <div className='min-h-screen bg-gradient-to-br from-gray-50 to-gray-100'>
            <TopBar title={'Virtual QR Payment'} url={-1} />

            <div className='flex justify-center items-start px-4 py-6 sm:py-8'>
                <div className='w-full max-w-md'>
                    <div className='bg-white rounded-2xl shadow-xl p-6 sm:p-8'>
                        <div className='text-center mb-6'>
                            <div className='w-16 h-16 bg-primary100 rounded-full flex items-center justify-center mx-auto mb-4'>
                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                            </div>
                            <h1 className='text-2xl sm:text-3xl font-bold text-gray-800 mb-2'>
                                Virtual QR Payment
                            </h1>
                            <p className='text-gray-600 text-sm sm:text-base leading-relaxed'>
                                Complete payment to generate your virtual QR code
                            </p>
                        </div>

                        {/* Patient Information */}
                        <div className='bg-gray-50 rounded-xl p-4 mb-6'>
                            <h3 className='text-sm font-semibold text-gray-800 mb-3'>Patient Information</h3>
                            <div className='space-y-2 text-sm'>
                                <div className='flex justify-between'>
                                    <span className='text-gray-600'>Name:</span>
                                    <span className='font-medium text-gray-800'>
                                        {(patient.firstname || patient.lastname)
                                            ? `${patient.firstname || ''} ${patient.lastname || ''}`.trim()
                                            : `Patient (${patient.system_id})`}
                                    </span>
                                </div>
                                <div className='flex justify-between'>
                                    <span className='text-gray-600'>Phone:</span>
                                    <span className='font-medium text-gray-800'>{patient.mobile || 'Provided'}</span>
                                </div>
                                {patient.email && (
                                    <div className='flex justify-between'>
                                        <span className='text-gray-600'>Email:</span>
                                        <span className='font-medium text-gray-800'>{patient.email}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Hospital Information */}
                        <div className='bg-blue-50 rounded-xl p-4 mb-6'>
                            <h3 className='text-sm font-semibold text-gray-800 mb-3'>Hospital Information</h3>
                            <div className='space-y-2 text-sm'>
                                <div className='flex justify-between'>
                                    <span className='text-gray-600'>Hospital:</span>
                                    <span className='font-medium text-gray-800'>{hospital.name}</span>
                                </div>
                                {hospital.contact && (
                                    <div className='flex justify-between'>
                                        <span className='text-gray-600'>Contact:</span>
                                        <span className='font-medium text-gray-800'>{hospital.contact}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Payment Details */}
                        <div className='bg-green-50 border border-green-200 rounded-xl p-4 mb-6'>
                            <h3 className='text-sm font-semibold text-gray-800 mb-3'>Payment Details</h3>
                            <div className='space-y-2'>
                                <div className='flex justify-between items-center'>
                                    <span className='text-gray-600'>Virtual QR Code Fee:</span>
                                    <span className='text-xl font-bold text-green-600'>
                                        {formatCurrency(virtualQrPrice || hospital.virtual_qr_price)}
                                    </span>
                                </div>
                                <div className='text-xs text-gray-500 mt-2'>
                                    This is a one-time fee to generate your virtual QR code for digital access to your medical records.
                                </div>
                            </div>
                        </div>

                        {/* Payment Information */}
                        <div className='bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6'>
                            <div className='flex items-start space-x-3'>
                                <div className='flex-shrink-0'>
                                    <svg className="w-5 h-5 text-yellow-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div>
                                    <p className='text-yellow-800 text-sm font-medium'>What happens after payment?</p>
                                    <ul className='text-yellow-700 text-xs mt-1 space-y-1'>
                                        <li>• Your virtual QR code will be generated</li>
                                        <li>• You can create your account password</li>
                                        <li>• Access your medical records digitally</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Payment Button */}
                        <button
                            onClick={handleInitiatePayment}
                            disabled={loading}
                            className={`w-full py-4 px-6 rounded-xl font-semibold text-lg transition-all duration-300 transform ${
                                loading
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:opacity-90 hover:scale-105 shadow-lg hover:shadow-xl'
                            }`}
                        >
                            {loading ? (
                                'Processing Payment...'
                            ) : (
                                `Pay ${formatCurrency(virtualQrPrice || hospital.virtual_qr_price)}`
                            )}
                        </button>

                        {/* Security Notice */}
                        <div className='mt-6 text-center'>
                            <div className='flex items-center justify-center space-x-6 text-xs text-gray-600'>
                                <div className='flex items-center space-x-2'>
                                    <svg className="w-4 h-4 text-primary100" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                    </svg>
                                    <span>Secure Payment</span>
                                </div>
                                <div className='flex items-center space-x-2'>
                                    <svg className="w-4 h-4 text-primary100" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    <span>Instant Processing</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}